import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import EmployerWorkflowStatus from '../constants/EmployerWorkflowStatus';

const ApplicationEventsContext = React.createContext({});

export const EVENT_TYPES = {
    BACK_TO_REVIEW: EmployerWorkflowStatus.FEEDBACK,
    MOVE_TO_CONTACT: EmployerWorkflowStatus.CONTACT,
    MOVE_TO_CONTACTED: EmployerWorkflowStatus.CONTACTED,
    MOVE_TO_INTERESTED: EmployerWorkflowStatus.REVIEWED,
    MOVE_TO_INTERVIEW: EmployerWorkflowStatus.INTERVIEW_AND_ASSESSMENT,
    MOVE_TO_HIRED: EmployerWorkflowStatus.HIRED,
    MOVE_TO_REJECT: EmployerWorkflowStatus.REJECTED,
    MOVE_TO_DECLINED: EmployerWorkflowStatus.DECLINED,
    CHANGE_HEADER_ACTIVE_TAB: 'CHANGE_HEADER_ACTIVE_TAB',
    ALLOW_CHANGE_ACTIVE_TAB: 'ALLOW_CHANGE_ACTIVE_TAB',
    CONTACT_CANDIDATE_EMAIL_FORM_CHANGE_TEMPLATE_SUBJECT: 'CONTACT_CANDIDATE_EMAIL_FORM_CHANGE_TEMPLATE_SUBJECT',
    CONTACT_CANDIDATE_EMAIL_FORM_CHANGE_TEMPLATE_BODY: 'CONTACT_CANDIDATE_EMAIL_FORM_CHANGE_TEMPLATE_BODY',
    CONTACT_CANDIDATE_FORM_CHANGE_TEMPLATE_BODY: 'CONTACT_CANDIDATE_FORM_CHANGE_TEMPLATE_BODY',
    EDITABLE_POSITION_SEARCH_CRITERIA_TEXT_CHANGE: 'EDITABLE_POSITION_SEARCH_CRITERIA_TEXT_CHANGE',
    EMAIL_FORM_TEXT_CHANGE: 'EMAIL_FORM_TEXT_CHANGE',
    CAMPAIGN_MESSAGE_EDITOR_FORM_CHANGE_TEMPLATE_SUBJECT: 'CAMPAIGN_MESSAGE_EDITOR_FORM_CHANGE_TEMPLATE_SUBJECT',
    CAMPAIGN_MESSAGE_EDITOR_FORM_CHANGE_TEMPLATE_BODY: 'CAMPAIGN_MESSAGE_EDITOR_FORM_CHANGE_TEMPLATE_BODY',
    CAMPAIGN_SENDER_TYPE_CHANGED: 'CAMPAIGN_SENDER_TYPE_CHANGED',
    CAMPAIGN_IMPORT_MESSAGES: 'CAMPAIGN_IMPORT_MESSAGES',
    CAMPAIGN_SYNCED: 'CAMPAIGN_SYNCED',
    MANAGE_JOB_POSITION_CRITERIA_UPDATED: 'MANAGE_JOB_POSITION_CRITERIA_UPDATED',
    MANAGE_JOB_POSITION_REFRESH: 'MANAGE_JOB_POSITION_REFRESH',
    ENABLE_RECOMMENDATIONS: 'ENABLE_RECOMMENDATIONS',
    DISABLE_RECOMMENDATIONS: 'DISABLE_RECOMMENDATIONS',
    BULK_RATE_CANDIDATES: 'BULK_RATE_CANDIDATES',
    DIVERSITY: 'AI_PANEL_DIVERSITY_TYPE_CHANGE',
    METADATA_EMAIL_TEMPLATE_FORM_CHANGE_TEMPLATE_SUBJECT: 'METADATA_EMAIL_TEMPLATE_FORM_CHANGE_TEMPLATE_SUBJECT',
    METADATA_EMAIL_TEMPLATE_FORM_CHANGE_TEMPLATE_BODY: 'METADATA_EMAIL_TEMPLATE_FORM_CHANGE_TEMPLATE_BODY',
    CONTACT_CANDIDATE_EMAIL_FORM_COPY_SUBJECT: 'CONTACT_CANDIDATE_EMAIL_FORM_COPY_SUBJECT',
    CONTACT_CANDIDATE_EMAIL_FORM_COPY_BODY: 'CONTACT_CANDIDATE_EMAIL_FORM_COPY_BODY',
};

export const AppWithEvents = (props) => {
    const { children } = props;

    const [, setEventListeners] = useState([]);

    const dispatchEvent = useCallback((event) => {
        setEventListeners((listeners) => {
            (listeners || []).forEach((listener) => {
                if (listener.eventType === event.type) {
                    listener.handler(event);
                }
            });

            return listeners;
        });
    }, []);

    const subscribe = useCallback((eventType, handler) => {
        setEventListeners((state) => {
            const isAlreadyExist = (state || []).some((eventData) => eventData.type === eventType
                && eventData.handler === handler);

            if (isAlreadyExist) {
                return state;
            }

            return [...state, { eventType, handler }];
        });
    }, []);

    const unsubscribe = useCallback((handler) => {
        setEventListeners((state) => (state || []).filter((listener) => handler !== listener.handler));
    }, []);

    const unsubscribeAll = useCallback((eventType) => {
        setEventListeners((state) => (state || []).filter((listener) => eventType !== listener.eventType));
    }, []);

    const contextValue = useMemo(() => ({
        subscribe,
        unsubscribe,
        unsubscribeAll,
        dispatchEvent,
    }), [
        subscribe,
        unsubscribe,
        unsubscribeAll,
        dispatchEvent,
    ]);

    return (
        <ApplicationEventsContext.Provider
            value={contextValue}
        >
            {children}
        </ApplicationEventsContext.Provider>
    );
};

AppWithEvents.propTypes = {
    children: PropTypes.node,
};

export default ApplicationEventsContext;
